import { directSupabase } from '../../../lib/supabaseClient';

function toBase64Url(bytes: Uint8Array): string {
  // Browser-safe base64url encoding
  let bin = '';
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
  const base64 = btoa(bin);
  return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
}

export function generateShareToken(byteLength: number = 32): string {
  const bytes = new Uint8Array(byteLength);
  crypto.getRandomValues(bytes);
  return toBase64Url(bytes);
}

export async function sha256Hex(input: string): Promise<string> {
  const data = new TextEncoder().encode(input);
  const digest = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(digest));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
}

export type CreatedDocumentShareLink = {
  id: string;
  url: string;
  token: string;
  createdAt: string;
};

export type ShareLinkSurveyArtefact = 'first' | 'final';

export type CreateDocumentShareLinkOptions = {
  sentVersionId?: string | null;
  sentVersionNumber?: number | null;
  /** Working version snapshotted into sentVersionId (immutable Transmit). */
  sourceVersionId?: string | null;
  /** Which survey this customer transmission belongs to. */
  surveyArtefact?: ShareLinkSurveyArtefact | null;
  sentNetTotal?: number | null;
  sentVatAmount?: number | null;
  sentGrossTotal?: number | null;
};

async function resolveShareUrlFromEmailActivity(shareLinkId: string): Promise<string | null> {
  const { data: row } = await directSupabase
    .from('email_activity_log')
    .select('metadata')
    .filter('metadata->>shareLinkId', 'eq', shareLinkId)
    .eq('status', 'sent')
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle();

  const meta = row?.metadata && typeof row.metadata === 'object' ? row.metadata : {};
  const candidate = String((meta as any)?.shareUrl || (meta as any)?.secureUrl || '').trim();
  return candidate || null;
}

export type DocumentShareLinkRecord = {
  id: string;
  documentId: string;
  sentVersionId: string | null;
  sentVersionNumber: number | null;
  /** Working version that was snapshotted into sentVersionId, when known. */
  sourceVersionId: string | null;
  /** first | final when recorded at transmit time. */
  surveyArtefact: ShareLinkSurveyArtefact | null;
  sentAt: string;
  createdAt: string;
  validUntil: string | null;
  revokedAt: string | null;
  revokedReason: string | null;
  url: string | null;
  isOutdated: boolean;
};

function getPublicBaseUrl(): string {
  // Configure this in environments where the app is not opened at the public domain
  // (e.g. when sending from localhost but customers should open a real domain).
  const configured = String(process.env.REACT_APP_PUBLIC_BASE_URL || '').trim();
  if (configured) return configured.replace(/\/+$/g, '');
  return window.location.origin;
}

function getPublicDocUrl(token: string): string {
  const baseUrl = getPublicBaseUrl();
  // Allows mapping to your desired public URL shape.
  // Examples:
  // - "/public/doc/{token}" (default)
  // - "/{token}" (requested: https://jarvis.keystonepropertysouth.co.uk/<token>)
  const template = String(process.env.REACT_APP_PUBLIC_DOC_PATH_TEMPLATE || '/public/doc/{token}').trim();
  const path = (template || '/public/doc/{token}').replace('{token}', encodeURIComponent(token));
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return `${baseUrl}${normalizedPath}`;
}

function uuidV4(): string {
  // Prefer the native API when available
  if (typeof crypto !== 'undefined' && typeof (crypto as any).randomUUID === 'function') {
    return (crypto as any).randomUUID();
  }

  // Fallback UUID v4 (RFC 4122) from random bytes
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes).map(b => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

function formatSupabaseError(e: any): string {
  const message = String(e?.message || '').trim();
  const code = String(e?.code || '').trim();
  const details = String(e?.details || '').trim();
  const hint = String(e?.hint || '').trim();
  const status = e?.status != null ? String(e.status).trim() : '';
  const name = String(e?.name || '').trim();

  const parts = [
    name ? `name=${name}` : '',
    message ? `message=${message}` : '',
    code ? `code=${code}` : '',
    status ? `status=${status}` : '',
    details ? `details=${details}` : '',
    hint ? `hint=${hint}` : '',
  ].filter(Boolean);

  if (parts.length > 0) return parts.join(' | ');

  // Last resort: include a safe serialization
  try {
    return `raw=${JSON.stringify(e)}`;
  } catch {
    return `raw=${String(e)}`;
  }
}

/**
 * Create a new share link token for a document.
 *
 * Notes:
 * - We store ONLY a hash of the token in DB (no plaintext token stored).
 * - If the same document version is resent and an active link exists, we reuse that URL.
 * - If no reusable link exists (new version, expired, nullified, or legacy gap), we mint a new token.
 */
export async function createDocumentShareLink(
  documentId: string,
  options: CreateDocumentShareLinkOptions = {}
): Promise<CreatedDocumentShareLink> {
  const nowIso = new Date().toISOString();
  const requestedVersionId = String(options.sentVersionId || '').trim();

  // If this exact version has already been sent and still has an active link,
  // reuse that same URL instead of minting a new one.
  if (requestedVersionId) {
    const { data: existingLinks, error: existingErr } = await directSupabase
      .from('document_share_links')
      .select('id, created_at, valid_until, revoked_at')
      .eq('document_id', documentId)
      .eq('sent_version_id', requestedVersionId)
      .is('revoked_at', null)
      .order('created_at', { ascending: false })
      .limit(10);

    if (existingErr) throw new Error(existingErr.message);

    const nowMs = Date.now();
    const reusableLink = (existingLinks || []).find((row: any) => {
      const validUntilIso = String(row?.valid_until || '').trim();
      if (!validUntilIso) return true;
      const validUntilMs = Date.parse(validUntilIso);
      return Number.isNaN(validUntilMs) || validUntilMs >= nowMs;
    });

    if (reusableLink?.id) {
      const reusedId = String(reusableLink.id);
      const reusedUrl = await resolveShareUrlFromEmailActivity(reusedId);
      if (reusedUrl) {
        await directSupabase
          .from('document_share_links')
          .update({ last_sent_at: nowIso })
          .eq('id', reusedId);

        return {
          id: reusedId,
          url: reusedUrl,
          token: '',
          createdAt: String(reusableLink.created_at || nowIso),
        };
      }
    }
  }

  const token = generateShareToken(32);
  const tokenHash = await sha256Hex(token);
  const linkId = uuidV4();

  const { data: userRes } = await directSupabase.auth.getUser();
  const userId = userRes.user?.id ?? null;

  const surveyArtefact = options.surveyArtefact === 'first' || options.surveyArtefact === 'final'
    ? options.surveyArtefact
    : null;

  // Important: don't rely on RETURNING/select() here.
  // Depending on RLS/policies, insert can succeed but select can fail.
  let error: any = null;
  try {
    const baseRow: Record<string, unknown> = {
      id: linkId,
      document_id: documentId,
      token_hash: tokenHash,
      sent_version_id: options.sentVersionId ?? null,
      sent_version_number: options.sentVersionNumber ?? null,
      source_version_id: options.sourceVersionId ?? null,
      sent_net_total: options.sentNetTotal ?? null,
      sent_vat_amount: options.sentVatAmount ?? null,
      sent_gross_total: options.sentGrossTotal ?? null,
      created_by: userId,
      last_sent_at: nowIso,
    };
    if (surveyArtefact) baseRow.survey_artefact = surveyArtefact;

    let res = await (directSupabase as any)
      .from('document_share_links')
      .insert([baseRow]);
    error = res?.error ?? null;
    // Older DBs without the column: retry without survey_artefact.
    if (error && surveyArtefact && /survey_artefact|schema cache/i.test(String(error.message || ''))) {
      delete baseRow.survey_artefact;
      res = await (directSupabase as any)
        .from('document_share_links')
        .insert([baseRow]);
      error = res?.error ?? null;
    }
  } catch (e: any) {
    // Network / fetch layer errors can throw rather than return { error }
    throw new Error(`Failed to create share link (${formatSupabaseError(e)})`);
  }

  if (error) {
    // Surface the real root cause (missing migration/table, RLS, etc.)
    const formatted = formatSupabaseError(error);
    const maybe404 = String((error as any)?.status || '').trim() === '404' || formatted.toLowerCase().includes('404');
    if (maybe404) {
      throw new Error(
        `Failed to create share link (${formatted}). ` +
          `This usually means the 'document_share_links' table is not deployed to Supabase yet (run migrations / db push).`
      );
    }
    throw new Error(`Failed to create share link (${formatted})`);
  }
  const createdAt = nowIso;

  // Public route is introduced in Phase 2; we mint the URL now so emails can include it.
  const url = getPublicDocUrl(token);

  return { id: linkId, createdAt, token, url };
}

export async function listDocumentShareLinks(documentId: string): Promise<DocumentShareLinkRecord[]> {
  const { data: links, error: linksErr } = await directSupabase
    .from('document_share_links')
    .select('*')
    .eq('document_id', documentId)
    .order('created_at', { ascending: false });
  if (linksErr) throw new Error(linksErr.message);

  const versionIds = Array.from(new Set(
    (links || [])
      .map((row: any) => String(row.sent_version_id || '').trim())
      .filter(Boolean)
  ));
  const versionNumberById = new Map<string, number>();
  if (versionIds.length > 0) {
    const { data: versionRows, error: versionErr } = await directSupabase
      .from('document_versions')
      .select('id,version_number')
      .in('id', versionIds);
    if (!versionErr) {
      (versionRows || []).forEach((row: any) => {
        const id = String(row.id || '').trim();
        if (!id) return;
        versionNumberById.set(id, Number(row.version_number));
      });
    }
  }

  const { data: logs, error: logsErr } = await directSupabase
    .from('email_activity_log')
    .select('created_at, metadata')
    .filter('metadata->>documentId', 'eq', documentId)
    .in('template_type', ['sendEstimateQuote', 'sendCustomerInvoice'])
    .eq('status', 'sent')
    .order('created_at', { ascending: false })
    .limit(300);
  if (logsErr) throw new Error(logsErr.message);

  const urlByShareLinkId = new Map<string, string>();
  const versionIdByShareLinkId = new Map<string, string>();
  const versionNumberByShareLinkId = new Map<string, number>();
  const parseVersionNumber = (value: unknown): number | null => {
    const n = Number(value);
    if (!Number.isFinite(n)) return null;
    const v = Math.trunc(n);
    return v > 0 ? v : null;
  };
  (logs || []).forEach((row: any) => {
    const meta = row?.metadata && typeof row.metadata === 'object' ? row.metadata : {};
    const shareLinkId = String((meta as any)?.shareLinkId || '').trim();
    const shareUrl = String((meta as any)?.shareUrl || (meta as any)?.secureUrl || '').trim();
    if (!shareLinkId) return;
    if (shareUrl && !urlByShareLinkId.has(shareLinkId)) {
      urlByShareLinkId.set(shareLinkId, shareUrl);
    }
    const sentVersionId = String(
      (meta as any)?.sentVersionId
      || (meta as any)?.sent_version_id
      || (meta as any)?.sentVersion?.id
      || ''
    ).trim();
    if (sentVersionId && !versionIdByShareLinkId.has(shareLinkId)) {
      versionIdByShareLinkId.set(shareLinkId, sentVersionId);
    }
    const sentVersionNumber = parseVersionNumber(
      (meta as any)?.sentVersionNumber
      ?? (meta as any)?.sent_version_number
      ?? (meta as any)?.sentVersion?.number
    );
    if (sentVersionNumber != null && !versionNumberByShareLinkId.has(shareLinkId)) {
      versionNumberByShareLinkId.set(shareLinkId, sentVersionNumber);
    }
  });

  const { data: allVersionRows, error: allVersionErr } = await directSupabase
    .from('document_versions')
    .select('id,version_number,created_at')
    .eq('document_id', documentId)
    .order('version_number', { ascending: false })
    .order('created_at', { ascending: false });
  if (allVersionErr) throw new Error(allVersionErr.message);
  const versionRowsTimeline: Array<{ id: string; versionNumber: number; createdAt: string }> = (allVersionRows || [])
    .map((r: any) => ({
      id: String(r?.id || '').trim(),
      versionNumber: Number(r?.version_number),
      createdAt: String(r?.created_at || '').trim(),
    }))
    .filter((r) => !!r.id && Number.isFinite(r.versionNumber));

  const normalized = (links || []).map((row: any) => {
    const sentAt = String(row.last_sent_at || row.created_at || '').trim() || new Date(0).toISOString();
    const linkId = String(row.id);
    const rowVersionId = String(row.sent_version_id || '').trim();
    const sentVersionId = rowVersionId || versionIdByShareLinkId.get(linkId) || null;
    let sentVersionNumber: number | null = null;
    if (row.sent_version_number != null) {
      sentVersionNumber = Number(row.sent_version_number);
    } else if (sentVersionId) {
      sentVersionNumber = versionNumberById.get(sentVersionId) ?? null;
    }
    if (sentVersionNumber == null) {
      sentVersionNumber = versionNumberByShareLinkId.get(linkId) ?? null;
    }
    if (sentVersionNumber == null) {
      const sentAtMs = Date.parse(sentAt);
      if (Number.isFinite(sentAtMs)) {
        const atSend = versionRowsTimeline.find((v) => {
          const createdMs = Date.parse(v.createdAt);
          return Number.isFinite(createdMs) && createdMs <= sentAtMs;
        });
        if (atSend) sentVersionNumber = atSend.versionNumber;
      }
    }
    const rawArtefact = String(row.survey_artefact || '').trim().toLowerCase();
    const surveyArtefact: ShareLinkSurveyArtefact | null =
      rawArtefact === 'first' || rawArtefact === 'final' ? rawArtefact : null;
    return {
      id: linkId,
      documentId: String(row.document_id),
      sentVersionId,
      sentVersionNumber,
      sourceVersionId: row.source_version_id ? String(row.source_version_id) : null,
      surveyArtefact,
      sentAt,
      createdAt: String(row.created_at),
      validUntil: row.valid_until ? String(row.valid_until) : null,
      revokedAt: row.revoked_at ? String(row.revoked_at) : null,
      revokedReason: row.revoked_reason ? String(row.revoked_reason) : null,
      url: urlByShareLinkId.get(String(row.id)) ?? null,
      isOutdated: false,
    } as DocumentShareLinkRecord;
  });

  const activeLinks = normalized
    .filter((row) => !row.revokedAt)
    .sort((a, b) => Date.parse(b.sentAt) - Date.parse(a.sentAt));
  const latestActiveSentAt = activeLinks[0] ? Date.parse(activeLinks[0].sentAt) : NaN;

  return normalized.map((row) => {
    const rowSentAt = Date.parse(row.sentAt);
    const isOutdated = Number.isFinite(latestActiveSentAt) && Number.isFinite(rowSentAt) && rowSentAt < latestActiveSentAt;
    return { ...row, isOutdated };
  });
}

export async function documentIdsWithCustomerTransmissions(
  documentIds: string[],
): Promise<Set<string>> {
  const ids = Array.from(new Set(documentIds.map((id) => String(id || '').trim()).filter(Boolean)));
  if (ids.length === 0) return new Set();
  const { data, error } = await directSupabase
    .from('document_share_links')
    .select('document_id')
    .in('document_id', ids);
  if (error) {
    console.warn('Failed to load customer invoice transmissions:', error.message);
    return new Set();
  }
  return new Set(
    (data || [])
      .map((row: { document_id?: string }) => String(row.document_id || '').trim())
      .filter(Boolean),
  );
}

export async function nullifyDocumentShareLink(linkId: string, reason?: string): Promise<void> {
  const { data: userRes } = await directSupabase.auth.getUser();
  const userId = userRes.user?.id ?? null;
  const revokedAt = new Date().toISOString();

  const { data, error } = await directSupabase
    .from('document_share_links')
    .update({
      revoked_at: revokedAt,
      revoked_by: userId,
      revoked_reason: String(reason || '').trim() || null,
    })
    .eq('id', linkId)
    .is('revoked_at', null)
    .select('id')
    .maybeSingle();
  if (error) throw new Error(error.message);
  if (!data?.id) throw new Error('Link is already nullified or was not found');
}

